import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Store, CheckCircle2, Circle, Gauge, ArrowUpRight } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, ProgressBar, Spinner, Chip, ScoreRing } from "../components/ui";
import { KYBVerification } from "../components/KYBVerification";

export default function BusinessCredit() {
  const [state, setState] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/vendors"),
      api.get("/business/foundation-score"),
      api.get("/funding/readiness"),
      api.get("/business/checklist"),
    ]).then(([v, f, fund, cl]) => setState({
      vendors: v.data, foundation: f.data, funding: fund.data, checklist: cl.data,
    })).catch(() => setState(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only data load
  }, []);

  if (state === null) return <Spinner />;
  if (state === false) return <div className="text-slate-400">Could not load business credit.</div>;

  const { vendors, foundation, funding, checklist } = state;
  const reporting = vendors.filter((v) => v.reporting);
  const approved = vendors.filter((v) => v.status === "approved");
  const creditItems = checklist.items.filter((i) => i.group === "credit");

  const milestones = [
    { label: "Business foundation started", done: foundation.score > 0 },
    { label: "EIN obtained", done: checklist.items.find((i) => i.key === "ein")?.done },
    { label: "Business bank account opened", done: checklist.items.find((i) => i.key === "bank_account")?.done },
    { label: "D-U-N-S registered", done: checklist.items.find((i) => i.key === "duns")?.done },
    { label: "First reporting vendor added", done: reporting.length >= 1 },
    { label: "3+ reporting tradelines", done: reporting.length >= 3 },
    { label: "Funding readiness above 60", done: funding.score >= 60 },
  ];

  return (
    <div className="space-y-6">
      <PageHeader label="Business Credit" title="Business Credit Dashboard"
        subtitle="Your business credit timeline, tradelines, reporting vendors, and growth at a glance." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Foundation", v: `${foundation.score}%`, icon: TrendingUp },
          { l: "Vendors", v: vendors.length, icon: Store },
          { l: "Reporting", v: reporting.length, icon: CheckCircle2 },
          { l: "Approved", v: approved.length, icon: CheckCircle2 },
        ].map((s) => (
          <GlassCard key={s.l} className="p-5">
            <s.icon size={16} className="text-blue-300" />
            <div className="cc-heading text-3xl font-700 text-white mt-3">{s.v}</div>
            <div className="text-sm text-slate-400 mt-0.5">{s.l}</div>
          </GlassCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Business Credit Milestones</h3>
          <div className="relative pl-6">
            <div className="absolute left-[9px] top-1 bottom-1 w-px bg-white/10" />
            <div className="space-y-4">
              {milestones.map((m, i) => (
                <div key={i} className="relative flex items-center gap-3">
                  <span className={`absolute -left-[22px] flex h-4 w-4 items-center justify-center rounded-full ${m.done ? "cc-gradient" : "cc-surface-2 border border-white/15"}`}>
                    {m.done && <CheckCircle2 size={10} className="text-white" />}
                  </span>
                  <span className={`text-sm ${m.done ? "text-white" : "text-slate-500"}`}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 flex flex-col items-center justify-center text-center">
          <ScoreRing value={funding.score} label="Funding" size={140} />
          <Link to="/credit/app/funding" className="mt-4 inline-flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200">
            Funding readiness <ArrowUpRight size={14} />
          </Link>
        </GlassCard>
      </div>

      <KYBVerification />

      <div className="grid lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="cc-heading text-lg font-500 text-white">Reporting Vendors & Tradelines</h3>
            <Link to="/credit/app/vendors" className="text-sm text-blue-300 hover:text-blue-200">Manage</Link>
          </div>
          {reporting.length === 0 ? (
            <p className="text-sm text-slate-500">No reporting vendors yet. Add Net-30 vendors that report to bureaus.</p>
          ) : (
            <div className="space-y-2">
              {reporting.map((v) => (
                <div key={v.id} className="flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-4 py-2.5">
                  <span className="text-sm text-slate-200">{v.name}</span>
                  <Chip tone="green">Reporting</Chip>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-4">Credit Profile Checklist</h3>
          <div className="space-y-2">
            {creditItems.map((i) => (
              <div key={i.key} className="flex items-center gap-3 text-sm">
                {i.done ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Circle size={16} className="text-slate-600" />}
                <span className={i.done ? "text-white" : "text-slate-400"}>{i.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <ProgressBar value={Math.round(creditItems.filter((i) => i.done).length / Math.max(1, creditItems.length) * 100)} />
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
